import React, { useState, useEffect } from 'react';
import { BookOpen, Save, Search, FileText, X } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import ExportMenu from '../common/ExportMenu';
import './MarksPage.css';

export default function MarksPage() {
  const { user } = useAuth();
  const [selectedClass, setSelectedClass] = useState(5);
  const [selectedSection, setSelectedSection] = useState('A');
  const [examName, setExamName] = useState('');
  const [examType, setExamType] = useState('unit-test');
  const [academicYear, setAcademicYear] = useState('2025-2026');
  const [subject, setSubject] = useState('');
  const [maxMarks, setMaxMarks] = useState('');

  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  const [stats, setStats] = useState(null);

  // Load students for selected class and section
  const handleLoadStudents = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const studentsResponse = await api.get(
        `/students?class=${selectedClass}&section=${selectedSection}&limit=100`
      );
      const studentsList = studentsResponse.data.students || studentsResponse.data || [];
      setStudents(studentsList);

      // Load existing marks if exam and subject are specified
      if (examName && subject) {
        await loadExistingMarks(selectedClass, examName, subject);
      } else {
        const initialMarks = {};
        const initialRemarks = {};
        studentsList.forEach(student => {
          initialMarks[student.id] = '';
          initialRemarks[student.id] = '';
        });
        setMarks(initialMarks);
        setRemarks(initialRemarks);
      }

      setMessage({ type: 'success', text: `Loaded ${studentsList.length} students` });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load students'
      });
      setStudents([]);
      setMarks({});
      setRemarks({});
    } finally {
      setLoading(false);
    }
  };

  // Load existing marks from API
  const loadExistingMarks = async (classNum, exam, subj) => {
    try {
      const response = await api.get(`/marks?class=${classNum}&exam=${exam}&subject=${subj}`);
      const marksData = {};
      const remarksData = {};

      const marksRecords = response.data.marks || response.data || [];
      marksRecords.forEach(record => {
        marksData[record.studentId] = record.obtainedMarks || '';
        remarksData[record.studentId] = record.remarks || '';
      });

      setMarks(marksData);
      setRemarks(remarksData);
    } catch (error) {
      console.error('Error loading existing marks:', error);
      // Initialize empty if no existing marks
      const initialMarks = {};
      const initialRemarks = {};
      students.forEach(student => {
        initialMarks[student.id] = '';
        initialRemarks[student.id] = '';
      });
      setMarks(initialMarks);
      setRemarks(initialRemarks);
    }
  };

  // Update marks
  const handleMarksChange = (studentId, value) => {
    setMarks({
      ...marks,
      [studentId]: value
    });
  };

  // Update remarks
  const handleRemarksChange = (studentId, value) => {
    setRemarks({
      ...remarks,
      [studentId]: value
    });
  };

  // Save marks
  const handleSaveMarks = async () => {
    if (students.length === 0) {
      setMessage({ type: 'error', text: 'No students loaded' });
      return;
    }

    if (!examName || !subject) {
      setMessage({ type: 'error', text: 'Please fill in exam name and subject' });
      return;
    }

    if (!maxMarks || isNaN(maxMarks)) {
      setMessage({ type: 'error', text: 'Please enter valid max marks' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const records = students
        .filter(student => marks[student.id] !== '' && marks[student.id] !== undefined)
        .map(student => ({
          studentId: student.id,
          class: selectedClass,
          section: selectedSection,
          examName: examName,
          examType: examType,
          academicYear: academicYear,
          subject: subject,
          maxMarks: parseInt(maxMarks),
          obtainedMarks: parseInt(marks[student.id]),
          remarks: remarks[student.id] || ''
        }));

      if (records.length === 0) {
        setMessage({ type: 'error', text: 'No marks entered for any student' });
        setLoading(false);
        return;
      }

      await api.post('/marks/bulk', { records });
      setMessage({ type: 'success', text: `Saved marks for ${records.length} student(s)` });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save marks'
      });
    } finally {
      setLoading(false);
    }
  };

  // View report card for a student
  const handleViewReport = async (student) => {
    setSelectedStudent(student);
    setShowReportModal(true);
    setReportLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.get(`/marks/report-card/${student.id}`);
      const rc = response.data.reportCard || response.data || {};
      setReportData(Object.values(rc));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load report card'
      });
    } finally {
      setReportLoading(false);
    }
  };

  // Load stats
  const handleLoadStats = async () => {
    if (!subject) {
      setMessage({ type: 'error', text: 'Please specify a subject' });
      return;
    }

    setLoading(true);

    try {
      const response = await api.get(`/marks/stats?subject=${subject}&class=${selectedClass}`);
      setStats(response.data);
      setMessage({ type: 'success', text: 'Stats loaded successfully' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load stats'
      });
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  // Close report modal
  const closeReportModal = () => {
    setShowReportModal(false);
    setSelectedStudent(null);
    setReportData([]);
  };

  return (
    <div className="marks-page">
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>
            <BookOpen size={28} />
            Student Marks Entry
          </h1>
          <ExportMenu
            endpoint="/marks/export"
            role={user?.role}
            baseName="marks"
            scopes={[
              {
                id: 'class',
                label: 'This class',
                hint: `Class ${selectedClass} — Section ${selectedSection}${examName ? ' · ' + examName : ''}`,
                params: {
                  class: selectedClass,
                  section: selectedSection,
                  ...(examName ? { examName } : {}),
                  ...(academicYear ? { academicYear } : {})
                }
              },
              {
                id: 'school',
                label: 'Whole school',
                hint: 'All classes · all exams',
                params: {
                  ...(academicYear ? { academicYear } : {})
                }
              }
            ]}
          />
        </div>

        <div className="card-body">
          {/* Message Display */}
          {message.text && (
            <div className={`message message-${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Stats Section */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Average Marks</div>
                <div className="stat-value">{stats.averageMarks?.toFixed(2) || 'N/A'}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Pass Rate</div>
                <div className="stat-value">{stats.passRate?.toFixed(1) || 'N/A'}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Students</div>
                <div className="stat-value">{stats.totalStudents || 0}</div>
              </div>
            </div>
          )}

          {/* Filter/Config Section */}
          <div className="filter-bar form-row">
            <div className="filter-group">
              <label htmlFor="class-select">Class</label>
              <select
                id="class-select"
                className="form-control"
                value={selectedClass}
                onChange={(e) => setSelectedClass(Number(e.target.value))}
                disabled={loading}
              >
                {Array.from({ length: 6 }, (_, i) => i + 5).map(classNum => (
                  <option key={classNum} value={classNum}>Class {classNum}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="section-select">Section</label>
              <select
                id="section-select"
                className="form-control"
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                disabled={loading}
              >
                <option value="A">Section A</option>
                <option value="B">Section B</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="exam-name">Exam Name</label>
              <input
                id="exam-name"
                type="text"
                className="form-control"
                placeholder="e.g., Unit Test 1"
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="exam-type">Exam Type</label>
              <select
                id="exam-type"
                className="form-control"
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                disabled={loading}
              >
                <option value="unit-test">Unit Test</option>
                <option value="mid-term">Mid Term</option>
                <option value="final">Final</option>
                <option value="assignment">Assignment</option>
                <option value="practical">Practical</option>
              </select>
            </div>
          </div>

          <div className="filter-bar form-row">
            <div className="filter-group">
              <label htmlFor="academic-year">Academic Year</label>
              <input
                id="academic-year"
                type="text"
                className="form-control"
                placeholder="e.g., 2025-2026"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                type="text"
                className="form-control"
                placeholder="e.g., Mathematics"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="max-marks">Max Marks</label>
              <input
                id="max-marks"
                type="number"
                className="form-control"
                placeholder="e.g., 50"
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
                disabled={loading}
              />
            </div>

            <button
              className="btn btn-primary"
              onClick={handleLoadStudents}
              disabled={loading}
              style={{ alignSelf: 'flex-end' }}
            >
              <Search size={18} />
              Load Students
            </button>
          </div>

          {/* Marks Entry Table */}
          {students.length > 0 && (
            <>
              <div className="table-container">
                <table className="marks-table">
                  <thead>
                    <tr>
                      <th>Roll No</th>
                      <th>Student Name</th>
                      <th>Obtained Marks</th>
                      <th>Remarks</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(student => (
                      <tr key={student.id}>
                        <td className="roll-col">{student.rollNo || '-'}</td>
                        <td className="name-col">
                          {student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim() || '—'}
                        </td>
                        <td className="marks-col">
                          <input
                            type="number"
                            className="form-control marks-input"
                            placeholder="0"
                            min="0"
                            max={maxMarks || 100}
                            value={marks[student.id] || ''}
                            onChange={(e) => handleMarksChange(student.id, e.target.value)}
                          />
                        </td>
                        <td className="remarks-col">
                          <input
                            type="text"
                            className="form-control remarks-input"
                            placeholder="e.g., Good performance"
                            value={remarks[student.id] || ''}
                            onChange={(e) => handleRemarksChange(student.id, e.target.value)}
                          />
                        </td>
                        <td className="action-col">
                          <button
                            className="btn-icon"
                            onClick={() => handleViewReport(student)}
                            title="View Report Card"
                          >
                            <FileText size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="action-bar">
                <button
                  className="btn btn-success"
                  onClick={handleSaveMarks}
                  disabled={loading}
                >
                  <Save size={18} />
                  Save Marks
                </button>
                {subject && (
                  <button
                    className="btn btn-secondary"
                    onClick={handleLoadStats}
                    disabled={loading}
                  >
                    View Stats
                  </button>
                )}
              </div>
            </>
          )}

          {students.length === 0 && !message.text && (
            <div className="empty-state">
              <BookOpen size={48} />
              <p>Fill in the exam details and click "Load Students" to begin entering marks.</p>
            </div>
          )}
        </div>
      </div>

      {/* Report Card Modal */}
      {showReportModal && (
        <div className="modal-overlay" onClick={closeReportModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Report Card - {selectedStudent?.name}</h2>
              <button className="close-btn" onClick={closeReportModal}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {reportLoading ? (
                <div className="loading">Loading report card...</div>
              ) : reportData.length > 0 ? (
                <div className="report-content">
                  {reportData.map((exam, index) => (
                    <div key={index} className="report-section">
                      <div className="report-title">{exam.examName} ({exam.examType})</div>
                      <div className="report-details">
                        <div className="detail-row">
                          <span className="detail-label">Subject:</span>
                          <span className="detail-value">{exam.subject}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Obtained Marks:</span>
                          <span className="detail-value">
                            {exam.obtainedMarks}/{exam.maxMarks}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Percentage:</span>
                          <span className="detail-value">
                            {((exam.obtainedMarks / exam.maxMarks) * 100).toFixed(2)}%
                          </span>
                        </div>
                        {exam.remarks && (
                          <div className="detail-row">
                            <span className="detail-label">Remarks:</span>
                            <span className="detail-value">{exam.remarks}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data">No marks found for this student</div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeReportModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
